import * as MenuPrimitive from '@radix-ui/react-dropdown-menu';
import { Check } from 'lucide-react';
import { forwardRef, type ComponentPropsWithoutRef } from 'react';
import { cn } from '@/shared/utils/cn';
import { Icon } from '../icons/Icon';

export const DropdownMenu = MenuPrimitive.Root;
export const DropdownMenuTrigger = MenuPrimitive.Trigger;
export const DropdownMenuRadioGroup = MenuPrimitive.RadioGroup;

const itemClass =
  'relative flex h-11 cursor-default select-none items-center gap-3 rounded-sm px-3 text-sm font-medium text-fg outline-none data-[disabled]:opacity-50 data-[highlighted]:bg-muted';

export const DropdownMenuContent = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof MenuPrimitive.Content>
>(function DropdownMenuContent({ className, sideOffset = 8, collisionPadding = 8, ...rest }, ref) {
  return (
    <MenuPrimitive.Portal>
      <MenuPrimitive.Content
        ref={ref}
        sideOffset={sideOffset}
        collisionPadding={collisionPadding}
        className={cn('anim-popover surface-float z-50 min-w-48 rounded-lg p-1', className)}
        {...rest}
      />
    </MenuPrimitive.Portal>
  );
});

export const DropdownMenuItem = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof MenuPrimitive.Item>
>(function DropdownMenuItem({ className, ...rest }, ref) {
  return <MenuPrimitive.Item ref={ref} className={cn(itemClass, className)} {...rest} />;
});

export const DropdownMenuRadioItem = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof MenuPrimitive.RadioItem>
>(function DropdownMenuRadioItem({ className, children, ...rest }, ref) {
  return (
    <MenuPrimitive.RadioItem
      ref={ref}
      className={cn(itemClass, 'justify-between', className)}
      {...rest}
    >
      <span className="flex items-center gap-3">{children}</span>
      <MenuPrimitive.ItemIndicator className="text-primary">
        <Icon icon={Check} size={16} />
      </MenuPrimitive.ItemIndicator>
    </MenuPrimitive.RadioItem>
  );
});

export const DropdownMenuLabel = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof MenuPrimitive.Label>
>(function DropdownMenuLabel({ className, ...rest }, ref) {
  return (
    <MenuPrimitive.Label
      ref={ref}
      className={cn(
        'px-3 pb-1 pt-2 text-xs font-semibold uppercase tracking-wider text-fg-muted',
        className,
      )}
      {...rest}
    />
  );
});

export const DropdownMenuSeparator = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof MenuPrimitive.Separator>
>(function DropdownMenuSeparator({ className, ...rest }, ref) {
  return (
    <MenuPrimitive.Separator
      ref={ref}
      className={cn('my-1 h-px bg-border-soft', className)}
      {...rest}
    />
  );
});
